import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import { fetchEntityIfNeeded, fetchEntity } from '@classpass/saga-tools';
import { defineMessages } from 'react-intl';
import _hasIn from 'lodash/hasIn';
import { makePath } from 'helpers/link-generation';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { waitForModal } from 'components/Modal';
import URLS from 'constants/urls';
import { ACTIVE_VENUE_CHANGED } from 'features/App/App.saga';
import {
  reviewsData,
  reviewsStatistics,
  ratingsClasses,
  ratingsInstructors,
  classesRatings,
  instructorsRatings,
} from 'store/saga/entities/bound';
import { selectActiveVenueId, selectRouteParams } from 'selectors/general';

import { reportError } from 'helpers/error-logging';
import {
  setCurrentPage,
  setHasMore,
  resetFilters,
  RESET_FILTERS,
  setShouldRefreshRatingsList,
  setSelectedClass,
  setSelectedInstructor,
  setRatingsUserFilter,
  setSelectedTab,
} from './Ratings.reducers';
import {
  selectReviews,
  selectCurrentPage,
  selectActiveFilters,
  selectSelectedInstructorId,
  selectSelectedClassId,
  selectRouteQueries,
  selectSelectedTab,
} from './Ratings.selectors';

const isAlreadyReportedError = ({ response }) =>
  _hasIn(response, 'data.error.message') && response.data.error.message === 'Abuse report for review already exists';

export const FETCH_REVIEWS_DATA = 'FETCH_REVIEWS_DATA';
export const fetchReviewsData = () => ({ type: FETCH_REVIEWS_DATA });

export const FETCH_REVIEWS_FIRST_PAGE = 'FETCH_REVIEWS_FIRST_PAGE';
export const fetchReviewsFirstPage = () => ({ type: FETCH_REVIEWS_FIRST_PAGE });

export const FETCH_REVIEWS_DATA_STATISTICS = 'FETCH_REVIEWS_DATA_STATISTICS';
export const fetchReviewsStatistics = () => ({ type: FETCH_REVIEWS_DATA_STATISTICS });

export const REPORT_REVIEW_AS_ABUSE = 'REPORT_REVIEW_AS_ABUSE';
export const reportReviewAsAbuse = reviewId => ({ type: REPORT_REVIEW_AS_ABUSE, reviewId });

export const DELETE_STUDIO_REPLY = 'DELETE_STUDIO_REPLY';
export const deleteStudioReply = reviewId => ({ type: DELETE_STUDIO_REPLY, reviewId });

export const REPLY_TO_REVIEW = 'REPLY_TO_REVIEW';
export const replyToReview = (reviewId, replyData) => ({
  type: REPLY_TO_REVIEW,
  reviewId,
  replyData,
});

export const FETCH_RATINGS_CLASSES = 'FETCH_RATINGS_CLASSES';
export const fetchRatingsClasses = () => ({ type: FETCH_RATINGS_CLASSES });

export const FETCH_RATINGS_INSTRUCTORS = 'FETCH_RATINGS_INSTRUCTORS';
export const fetchRatingsInstructors = () => ({ type: FETCH_RATINGS_INSTRUCTORS });

export const FETCH_CLASS_RATINGS = 'FETCH_CLASS_RATINGS';
export const fetchClassesRatings = () => ({ type: FETCH_CLASS_RATINGS });

export const FETCH_INSTRUCTORS_RATINGS = 'FETCH_INSTRUCTORS_RATINGS';
export const fetchInstructorsRatings = () => ({ type: FETCH_INSTRUCTORS_RATINGS });

export const UPDATE_INSTRUCTOR = 'UPDATE_INSTRUCTOR';
export const updateInstructor = instructorId => ({ type: UPDATE_INSTRUCTOR, instructorId });

export const UPDATE_CLASS = 'UPDATE_CLASS';
export const updateClass = classId => ({ type: UPDATE_CLASS, classId });

export const UPDATE_USER_FILTER = 'UPDATE_USER_FILTER';
export const updateUserFilter = key => ({ type: UPDATE_USER_FILTER, key });

export const UPDATE_SELECTED_TAB = 'UPDATE_SELECTED_TAB';
export const updateSelectedTab = tab => ({ type: UPDATE_SELECTED_TAB, tab });

export const messages = {
  success: defineMessages({
    reviewReported: {
      id: 'ratings.saga.success.review-reported',
      defaultMessage: 'Thank you for reporting this review. Your report has been successfully received.',
    },
    replyDeleted: {
      id: 'ratings.saga.success.reply-deleted',
      defaultMessage: 'Your reply has been deleted.',
    },
    replySaved: {
      id: 'ratings.saga.success.reply-saved.',
      defaultMessage: 'Your reply has been saved.',
    },
    reviewReportedBefore: {
      id: 'ratings.saga.success.review-reported-before',
      defaultMessage: 'Thank you for reporting this review. This review has already been reported.',
    },
  }),
  error: defineMessages({
    problemWithRequest: {
      id: 'ratings.saga.error.problem-with-request',
      defaultMessage: 'There was a problem with your request.',
    },
    4002: {
      id: 'ratings.saga.error.flagged-words',
      defaultMessage: 'Your response contains flagged words. Please modify it and try posting again.',
    },
  }),
};

export function* doFetchReviewsData(client) {
  try {
    const [venueId, previousReviewsData, currentPage, activeFilters] = yield all([
      select(selectActiveVenueId),
      select(selectReviews),
      select(selectCurrentPage),
      select(selectActiveFilters),
    ]);

    const { currentPage: loadedPageNumber, hasMore } = yield call(fetchEntityIfNeeded, reviewsData, [
      client,
      venueId,
      currentPage + 1,
      activeFilters,
      previousReviewsData,
    ]);
    yield all([put(setCurrentPage(loadedPageNumber)), put(setHasMore(hasMore))]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchReviewsFirstPage(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const activeFilters = yield select(selectActiveFilters);
    const { currentPage, hasMore } = yield call(fetchEntity, reviewsData, [client, venueId, 0, activeFilters]);
    yield all([put(setCurrentPage(currentPage)), put(setHasMore(hasMore)), put(setShouldRefreshRatingsList(true))]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchReviewsStatistics(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const activeFilters = yield select(selectActiveFilters);
    yield call(fetchEntityIfNeeded, reviewsStatistics, [client, venueId, activeFilters]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doReportReviewAsAbuse(client, action) {
  try {
    const { httpClient } = client;
    const { reviewId } = action;
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.ratings.reportReview(venueId, reviewId);

    const confirmed = yield call(waitForModal, client, 'ReportReviewConfirmationModal');
    if (!confirmed) return;

    yield call([httpClient, httpClient.post], endpoint);
    yield put(createInfoBanner(messages.success.reviewReported));
  } catch (error) {
    if (isAlreadyReportedError(error)) {
      yield put(createInfoBanner(messages.success.reviewReportedBefore));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doDeleteStudioReply(client, action) {
  try {
    const { httpClient } = client;
    const { reviewId } = action;
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.ratings.removeResponse(venueId, reviewId);

    const confirmed = yield call(waitForModal, client, 'RemoveStudioResponseConfirmationModal');
    if (!confirmed) return;

    yield call([httpClient, httpClient.delete], endpoint);
    yield put(createInfoBanner(messages.success.replyDeleted));
    yield put(fetchReviewsFirstPage());
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doReplyToReview(client, action) {
  try {
    const { httpClient } = client;
    const venueId = yield select(selectActiveVenueId);
    const { reviewId, replyData } = action;
    const endpoint = URLS.ratings.respond(venueId, reviewId);

    yield call([httpClient, httpClient.post], endpoint, replyData);
    yield put(createInfoBanner(messages.success.replySaved));
    yield put(fetchReviewsFirstPage());
  } catch (error) {
    const errorCode = error.response?.data?.error?.code || 0;
    // We specifically check for 4002 since this is the only error code handled right now.
    if (errorCode === 4002) {
      yield put(createErrorBanner(messages.error[errorCode]));
    } else {
      yield put(createErrorBanner(messages.error.problemWithRequest));
    }
    yield call(reportError, error);
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  yield put(resetFilters());
  yield call(client.navigate, makePath.ratings({ venueId }));
}

export function* doFetchRatingsClasses(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntityIfNeeded, ratingsClasses, [client, venueId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchRatingsInstructors(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntityIfNeeded, ratingsInstructors, [client, venueId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchClassesRatings(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const instructorId = yield select(selectSelectedInstructorId);
    yield call(fetchEntityIfNeeded, classesRatings, [client, venueId, instructorId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doFetchInstructorsRatings(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const classId = yield select(selectSelectedClassId);
    yield call(fetchEntityIfNeeded, instructorsRatings, [client, venueId, classId]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doUpdateInstructor(client, action) {
  const venueId = yield select(selectActiveVenueId);
  const { instructorId } = action;
  const tab = yield select(selectSelectedTab);
  const { classId, userFilterKey } = yield select(selectActiveFilters);

  const newPath = makePath.ratings({ venueId, classId, instructorId, initialVisits: userFilterKey, tab });

  yield call(client.navigate, newPath);
}

export function* doUpdateClass(client, action) {
  const venueId = yield select(selectActiveVenueId);
  const tab = yield select(selectSelectedTab);
  const { classId } = action;
  const { instructorId, userFilterKey } = yield select(selectActiveFilters);

  const newPath = makePath.ratings({ venueId, classId, instructorId, initialVisits: userFilterKey, tab });
  yield call(client.navigate, newPath);
}

export function* doUpdateUserFilter(client, action) {
  const venueId = yield select(selectActiveVenueId);
  const tab = yield select(selectSelectedTab);
  const { key } = action;
  const { instructorId, classId } = yield select(selectActiveFilters);

  const newPath = makePath.ratings({ venueId, classId, instructorId, initialVisits: key, tab });
  yield call(client.navigate, newPath);
}

export function* doUpdateSelectedTab(client, action) {
  const venueId = yield select(selectActiveVenueId);
  const { tab } = action;
  const { instructorId, classId, userFilterKey } = yield select(selectActiveFilters);

  const newPath = makePath.ratings({ venueId, classId, instructorId, initialVisits: userFilterKey, tab });
  yield call(client.navigate, newPath);
}

export function* handleResetFilters(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const newPath = makePath.ratings({ venueId });

    yield call(client.navigate, newPath);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* handleQueryParamsChange() {
  const { classId, instructorId, userFilterKey, tab } = yield select(selectRouteQueries);

  yield all([
    put(setSelectedClass(classId)),
    put(setSelectedInstructor(instructorId)),
    put(setRatingsUserFilter(userFilterKey)),
    put(setSelectedTab(tab)),
  ]);
}

export function* handleRouteEnter(client) {
  try {
    const { tab: routeTab, venueId } = yield select(selectRouteParams);
    if (!venueId) {
      const activeVenueId = yield select(selectActiveVenueId);
      yield call(client.navigate, makePath.ratings({ venueId: activeVenueId, tab: routeTab }));
    }
    const { classId, instructorId, userFilterKey, tab } = yield select(selectRouteQueries);

    yield all([
      put(setSelectedClass(classId)),
      put(setSelectedInstructor(instructorId)),
      put(setRatingsUserFilter(userFilterKey)),
      put(setSelectedTab(tab)),
      put(fetchReviewsFirstPage()),
      put(fetchReviewsStatistics()),
      put(fetchRatingsClasses()),
      put(fetchRatingsInstructors()),
      put(fetchClassesRatings()),
      put(fetchInstructorsRatings()),
    ]);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* watchRatingsActions(client) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(FETCH_REVIEWS_DATA, doFetchReviewsData, client);
  yield takeEvery(FETCH_REVIEWS_FIRST_PAGE, doFetchReviewsFirstPage, client);
  yield takeEvery(UPDATE_USER_FILTER, doUpdateUserFilter, client);
  yield takeEvery(UPDATE_CLASS, doUpdateClass, client);
  yield takeEvery(UPDATE_INSTRUCTOR, doUpdateInstructor, client);
  yield takeEvery(UPDATE_SELECTED_TAB, doUpdateSelectedTab, client);
  yield takeEvery(RESET_FILTERS, handleResetFilters, client);
  yield takeEvery(FETCH_REVIEWS_DATA_STATISTICS, doFetchReviewsStatistics, client);
  yield takeEvery(REPORT_REVIEW_AS_ABUSE, doReportReviewAsAbuse, client);
  yield takeEvery(DELETE_STUDIO_REPLY, doDeleteStudioReply, client);
  yield takeEvery(REPLY_TO_REVIEW, doReplyToReview, client);
  yield takeEvery(FETCH_RATINGS_CLASSES, doFetchRatingsClasses, client);
  yield takeEvery(FETCH_RATINGS_INSTRUCTORS, doFetchRatingsInstructors, client);
  yield takeEvery(FETCH_CLASS_RATINGS, doFetchClassesRatings, client);
  yield takeEvery(FETCH_INSTRUCTORS_RATINGS, doFetchInstructorsRatings, client);
}
